import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Instagram, 
  Youtube, 
  MessageSquare, 
  Linkedin, 
  Twitter,
  Hash,
  MessageCircle,
  ExternalLink,
  Clock,
  DollarSign,
  MoreVertical,
  Copy,
  Zap,
  TrendingUp
} from 'lucide-react';
import { formatTimestamp } from '../lib/timestamps';
import { isQuickWin } from '../lib/quickWin';

const getChannelIcon = (channel) => {
  switch (channel?.toLowerCase()) {
    case 'instagram':
      return Instagram;
    case 'youtube':
      return Youtube;
    case 'reddit':
      return MessageSquare;
    case 'linkedin':
      return Linkedin;
    case 'x':
      return Twitter;
    case 'discord':
      return Hash;
    case 'feedback':
      return MessageCircle;
    default:
      return MessageCircle;
  }
};

const getUrgencyClass = (urgency) => {
  if (urgency >= 4) return 'urgency-high';
  if (urgency >= 3) return 'urgency-medium';
  return 'urgency-low';
};

const formatCurrency = (amount) => {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}k`;
  }
  return `$${amount}`;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'planned':
      return 'bg-blue-500';
    case 'in_progress':
      return 'bg-amber-500';
    case 'shipped':
      return 'bg-green-500';
    default:
      return 'bg-gray-500';
  }
};

const DecisionCard = ({ decision, onUpdateStatus, onEdit, onDelete, selectable = false, selected = false, onToggleSelect }) => {
  const [showSimilar, setShowSimilar] = useState(false);
  
  if (!decision || !decision.signal) return null;

  const ChannelIcon = getChannelIcon(decision.signal.channel);
  const urgencyClass = getUrgencyClass(decision.urgency);
  const hasRevenue = decision.revenue_estimate && decision.revenue_estimate.potential_mrr > 0;
  const hasSimilar = decision.similar_signals && decision.similar_signals.length > 0;

  return (
    <Card 
      className={`p-4 space-y-3 hover:shadow-md transition-shadow ${urgencyClass} ${selected ? 'ring-2 ring-primary' : ''}`}
      data-testid="decision-card"
    >
      {/* Header: Channel + Time + Status */}
      <div className="flex items-center justify-between">
        <div 
          className="flex items-center gap-2 text-xs text-muted-foreground"
          data-testid="decision-card-source"
        >
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => { e.stopPropagation(); onToggleSelect?.(decision.signal_id); }}
              className="h-3.5 w-3.5 accent-primary cursor-pointer"
              aria-label={`Select ${decision.summary}`}
              data-testid={`decision-select-${decision.signal_id}`}
            />
          )}
          <ChannelIcon className="h-3.5 w-3.5" />
          <span className="capitalize">{decision.signal.channel}</span>
          {decision.status && decision.status !== 'new' && (
            <Badge className={`text-xs ${getStatusColor(decision.status)} text-white`}>
              {decision.status.replace('_', ' ')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div 
            className="flex items-center gap-1 text-xs text-muted-foreground font-mono"
            data-testid="decision-card-timestamp"
          >
            <Clock className="h-3 w-3" />
            <span>{formatTimestamp(decision.signal.timestamp)}</span>
          </div>
          
          {/* Actions dropdown */}
          {(onUpdateStatus || onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`decision-menu-${decision.signal_id}`}>
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onUpdateStatus && (
                  <>
                    <DropdownMenuItem onClick={() => onUpdateStatus(decision.signal_id, 'planned')}>
                      Mark as Planned
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onUpdateStatus(decision.signal_id, 'in_progress')}>
                      Mark as In Progress
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onUpdateStatus(decision.signal_id, 'shipped')}>
                      Mark as Shipped
                    </DropdownMenuItem>
                  </>
                )}
                {onEdit && (
                  <DropdownMenuItem
                    onClick={() => onEdit(decision)}
                    data-testid={`decision-edit-${decision.signal_id}`}
                  >
                    Edit decision
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={() => onDelete(decision)}
                    className="text-destructive focus:text-destructive"
                    data-testid={`decision-delete-${decision.signal_id}`}
                  >
                    Delete decision
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Title */}
      <div>
        <h3 className="text-sm font-semibold mb-1">{decision.summary}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge 
            variant="secondary" 
            className="text-xs"
            data-testid="decision-card-segment"
          >
            {decision.signal.user_segment}
          </Badge>
          
          {decision.urgency >= 4 && (
            <Badge variant="destructive" className="text-xs">
              Urgent
            </Badge>
          )}
          
          {decision.revenue_impact >= 4 && (
            <Badge className="text-xs bg-green-500 text-white">
              High ROI
            </Badge>
          )}
          
          {/* Revenue Estimate Badge */}
          {hasRevenue && (
            <Badge className="text-xs bg-green-600 text-white flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              {formatCurrency(decision.revenue_estimate.potential_mrr)}/mo
            </Badge>
          )}
          
          {/* Quick Win Badge */}
          {isQuickWin(decision) && (
            <Badge className="text-xs bg-purple-600 text-white flex items-center gap-1 animate-pulse-once">
              <Zap className="h-3 w-3" />
              Quick Win
            </Badge>
          )}
        </div>
      </div>

      <Separator />

      {/* Signal text */}
      <div data-testid="decision-card-signal">
        <p className="text-xs text-muted-foreground line-clamp-2">
          "{decision.signal.text}"
        </p>
      </div>

      {/* Revenue Estimate Detail (for Build Next items) */}
      {hasRevenue && (
        <div className="bg-green-50 dark:bg-green-950/20 rounded-md p-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-green-700 dark:text-green-400">Revenue Potential</span>
            <Badge variant="outline" className="text-xs">
              {decision.revenue_estimate.confidence} confidence
            </Badge>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Monthly:</span>
            <span className="font-semibold">{formatCurrency(decision.revenue_estimate.potential_mrr)}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Annual:</span>
            <span className="font-semibold">{formatCurrency(decision.revenue_estimate.potential_arr)}</span>
          </div>
        </div>
      )}



      {/* Effort & ROI Display */}
      {decision.effort_hours && decision.decision_lane === 'build_next' && (
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-md p-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">Effort & ROI</span>
            {isQuickWin(decision) && (
              <Badge className="text-xs bg-purple-500 text-white">
                <Zap className="h-3 w-3 mr-1" />
                Quick Win
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Effort:</span>
              <span className="font-semibold">{decision.effort_display || `${decision.effort_hours}h`}</span>
            </div>
            {decision.roi_per_hour > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">ROI/hour:</span>
                <span className="font-semibold text-green-600 dark:text-green-400 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  {formatCurrency(decision.roi_per_hour)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Analysis */}
      <div 
        className="bg-muted/50 rounded-md p-3 space-y-2"
        data-testid="decision-card-analysis"
      >
        <div>
          <p className="text-xs font-semibold mb-1">Why this matters:</p>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {decision.why}
          </p>
        </div>
        
        <div>
          <p className="text-xs font-semibold mb-1">Next step:</p>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {decision.suggested_next_step}
          </p>
        </div>
      </div>

      {/* Similar Signals */}
      {hasSimilar && (
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSimilar(!showSimilar)}
            className="text-xs h-7"
          >
            <Copy className="h-3 w-3 mr-1" />
            {decision.similar_signals.length} similar signal{decision.similar_signals.length > 1 ? 's' : ''}
            <span className="ml-1">{showSimilar ? '▼' : '▶'}</span>
          </Button>
          
          {showSimilar && (
            <div className="pl-4 space-y-1 border-l-2 border-muted">
              {decision.similar_signals.map((similar, i) => (
                <div key={i} className="text-xs text-muted-foreground">
                  <span className="font-semibold">{Math.round(similar.similarity * 100)}% match:</span>
                  <span className="ml-1">{similar.text}...</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scores */}
      <div className="flex items-center gap-3 text-xs">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Urgency:</span>
          <span className="font-semibold">{decision.urgency}/5</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Revenue:</span>
          <span className="font-semibold">{decision.revenue_impact}/5</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Churn:</span>
          <span className="font-semibold">{decision.churn_risk}/5</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <Button 
          size="sm" 
          variant="outline" 
          className="text-xs flex-1"
          data-testid="decision-open-button"
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          View Details
        </Button>
      </div>
    </Card>
  );
};

export default DecisionCard;
